import React, { useState, useEffect, useRef } from 'react'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Typography,
  CircularProgress
} from '@mui/material'
import EditIcon from '@mui/icons-material/Edit'

interface RenameDialogProps {
  open: boolean
  onClose: () => void
  onConfirm: (newName: string) => Promise<void>
  currentName: string
  originalName: string
  isRenaming?: boolean
}

export default function RenameDialog({
  open,
  onClose,
  onConfirm,
  currentName,
  originalName,
  isRenaming = false
}: RenameDialogProps) {
  const [name, setName] = useState(currentName)
  const inputRef = useRef<HTMLInputElement>(null)

  // Sincroniza o nome ao abrir
  useEffect(() => {
    if (open) {
      setName(currentName)
      // Foca e seleciona todo o texto ao abrir
      setTimeout(() => inputRef.current?.select(), 100)
    }
  }, [open, currentName])

  const handleConfirm = async () => {
    const trimmed = name.trim()
    if (!trimmed) return
    await onConfirm(trimmed)
  }

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !isRenaming && name.trim()) {
      handleConfirm()
    }
  }

  const isOriginalName = name.trim() === originalName
  const hasCustomName = currentName !== originalName

  return (
    <Dialog
      open={open}
      onClose={isRenaming ? undefined : onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{ sx: { borderRadius: 3 } }}
    >
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, pb: 1 }}>
        <EditIcon sx={{ color: '#1976d2', fontSize: 22 }} />
        Renomear transcrição
      </DialogTitle>

      <DialogContent sx={{ pt: '8px !important' }}>
        <TextField
          inputRef={inputRef}
          autoFocus
          fullWidth
          label="Nome de exibição"
          value={name}
          onChange={(e) => setName(e.target.value)}
          onKeyDown={handleKeyDown}
          disabled={isRenaming}
          variant="outlined"
          size="small"
          sx={{ mb: 1.5 }}
          inputProps={{ maxLength: 200 }}
        />

        <Typography variant="caption" sx={{ color: '#999', display: 'block' }}>
          Nome original: {originalName}
        </Typography>

        {hasCustomName && (
          <Typography
            variant="caption"
            sx={{
              color: '#1976d2',
              cursor: 'pointer',
              mt: 0.5,
              display: 'inline-block',
              '&:hover': { textDecoration: 'underline' }
            }}
            onClick={() => setName(originalName)}
          >
            Restaurar nome original
          </Typography>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} disabled={isRenaming} color="inherit">
          Cancelar
        </Button>
        <Button
          onClick={handleConfirm}
          disabled={isRenaming || !name.trim()}
          variant="contained"
          startIcon={isRenaming ? <CircularProgress size={16} /> : undefined}
        >
          {isRenaming ? 'Salvando...' : 'Salvar'}
        </Button>
      </DialogActions>
    </Dialog>
  )
}
